import { HttpApiEndpoint, HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { DataIntegrityError, DatabaseError } from "../auth/errors";
import { Authorization } from "../auth/middleware";
import { ViewFolderId, ViewId } from "../ids/definitions";

export const ViewResponse = Schema.Struct({
  id: ViewId,
  name: Schema.String,
  table: Schema.String,
  filters: Schema.Array(Schema.Unknown),
  filterGroup: Schema.NullOr(Schema.Unknown),
  columns: Schema.Array(Schema.Unknown),
  sort: Schema.NullOr(Schema.Unknown),
  pageSize: Schema.NullOr(Schema.Number),
  folderId: Schema.NullOr(ViewFolderId),
  position: Schema.Number,
  deleted: Schema.NullOr(Schema.Boolean),
  isNotEditable: Schema.optional(Schema.Boolean),
});
export type ViewResponse = typeof ViewResponse.Type;

export const FolderResponse = Schema.Struct({
  id: ViewFolderId,
  table: Schema.String,
  name: Schema.String,
  position: Schema.Number,
  isPrivate: Schema.Boolean,
});
export type FolderResponse = typeof FolderResponse.Type;

const GetViewsAndFoldersResponse = Schema.Struct({
  views: Schema.Array(ViewResponse),
  folders: Schema.Array(FolderResponse),
});

const CreateFolderInput = Schema.Struct({
  id: Schema.optional(ViewFolderId),
  name: Schema.String,
  viewIds: Schema.Array(Schema.String),
  isPrivate: Schema.Boolean,
});

const CreateFolderResponse = Schema.Struct({ folderId: ViewFolderId });

const UpdateFolderInput = Schema.Struct({
  name: Schema.String,
  viewIds: Schema.Array(Schema.String),
});

const SaveViewsAndFoldersInput = Schema.Struct({
  views: Schema.Array(ViewResponse),
  folders: Schema.Array(FolderResponse),
});

const CreateViewInput = Schema.Struct({
  id: Schema.optional(ViewId),
  name: Schema.String,
  filterGroup: Schema.NullOr(Schema.Unknown),
  folderId: Schema.NullOr(ViewFolderId),
});

const CreateViewResponse = Schema.Struct({ viewId: ViewId });

const UpdateViewInput = Schema.Struct({
  name: Schema.String,
  filterGroup: Schema.NullOr(Schema.Unknown),
});

export const ViewFolderGroup = HttpApiGroup.make("viewFolder")
  .add(
    HttpApiEndpoint.get("getViewsAndFolders", "/view-folders")
      .middleware(Authorization)
      .addSuccess(GetViewsAndFoldersResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.post("createFolder", "/view-folders")
      .middleware(Authorization)
      .setPayload(CreateFolderInput)
      .addSuccess(CreateFolderResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.patch("updateFolder", "/view-folders/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ViewFolderId }))
      .setPayload(UpdateFolderInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.del("deleteFolder", "/view-folders/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ViewFolderId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.post("saveViewsAndFolders", "/view-folders/reorder")
      .middleware(Authorization)
      .setPayload(SaveViewsAndFoldersInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.post("createView", "/view-folders/views")
      .middleware(Authorization)
      .setPayload(CreateViewInput)
      .addSuccess(CreateViewResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.patch("updateView", "/view-folders/views/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ViewId }))
      .setPayload(UpdateViewInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.del("deleteView", "/view-folders/views/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ViewId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
